/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { Rect } from '@lib/canvasx/components/Rect';
import { canvasCssValue } from '@lib/canvasx/util';
import { Style } from '@lib/canvasx/components/Style';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import {
  FlexRow,
  type RenderedDimensions,
} from '@lib/canvasx/components/FlexRow';
import Color from 'color';
import type { WhiteboardRecordInfo } from '../shared-types';
import RecordDecorator, { RECORD_LINE_HEIGHT } from '../shapes/RecordDecorator';
import { Text } from '@lib/canvasx/components/Text';
import { FaIcon } from '../canvasx/FaIcon';
import { AhaIcon } from '../canvasx/AhaIcon';
import { UserAvatar } from '../canvasx/UserAvatar';
import { Image } from '@lib/canvasx/components/Image';
import AiIcon from '@aha-app/aha-base/src/components/aha-ai-icon/icon.svg';
import InterviewPlaceholderImage from '../assets/images/discoveryThumbnailPlaceholder.svg';
import { svgToURL } from '../utils/svg';

export type HighlightCardProps = {
  setRenderedDimensions?: (dimensions: RenderedDimensions) => void;
  record: WhiteboardRecordInfo;
  sanitizedText?: string;
} & ShapeStruct;

export const CARD_BORDER_RADIUS = 5;
export const CARD_PADDING: SpacingAxisStruct = { horizontal: 9, vertical: 9 };
export const RECORD_ICON_SIZE = 14;
export const HIGHLIGHT_FONT_SIZE = 13;

const HighlightCard: CanvaSXFC<HighlightCardProps> = function ({
  width,
  height,
  setRenderedDimensions,
  record,
  sanitizedText,
}) {
  const themeBackground = canvasCssValue(
    '--theme-secondary-background',
    '#f7f7f7'
  );
  const opacity = 1;

  const iconBackgroundColor = record => {
    switch (record.type) {
      case 'QUOTE':
        return '#DDE5FF';
      case 'LEARNING':
        return '#C6F3E0';
      case 'RECORD':
        return '#e0eef9';
      case 'SUMMARY':
        return '#f1e9f8';
      case 'CLIP':
        return '#ddecf0';
      default:
        return canvasCssValue('--theme-primary-border', '#e1e1e1');
    }
  };

  const thumbnailImageWidth = width - CARD_PADDING.horizontal * 2;

  function calculateIconX(size) {
    return (30 - size) / 2;
  }

  function calculateIconY(size) {
    return (20 - size) / 2;
  }

  const icon = record => {
    if (record.typename !== 'Annotation') {
      return null;
    }

    const iconX = calculateIconX(RECORD_ICON_SIZE);
    const iconY = calculateIconY(RECORD_ICON_SIZE);

    switch (record.type) {
      case 'QUOTE':
        return (
          <FaIcon
            size={RECORD_ICON_SIZE}
            icon='quote-left'
            style='solid'
            x={iconX}
            y={iconY}
            color={'#3a5acf'}
          />
        );
      case 'LEARNING':
        return (
          <FaIcon
            size={RECORD_ICON_SIZE}
            icon='graduation-cap'
            style='regular'
            x={iconX}
            y={iconY}
            color={'#018454'}
          />
        );
      case 'RECORD': {
        const recordSize = RECORD_ICON_SIZE + 3; // bump up size since icon is smaller
        return (
          <AhaIcon
            size={recordSize}
            icon='records'
            x={calculateIconX(recordSize)}
            y={calculateIconY(recordSize)}
            color={'#0072cf'}
          />
        );
      }
      case 'SUMMARY':
        return (
          <Image
            src={AiIcon}
            width={RECORD_ICON_SIZE}
            height={RECORD_ICON_SIZE}
            x={iconX}
            y={iconY}
          />
        );
      case 'CLIP':
        return (
          <FaIcon
            size={RECORD_ICON_SIZE}
            icon='video-camera'
            style='regular'
            x={iconX}
            y={iconY}
            color={'#397e82'}
          />
        );
      default:
        return RecordDecorator.defaultIconForType(record.typename);
    }
  };

  if (record.typename !== 'Annotation') {
    return null;
  }

  return (
    <Style
      fill={{
        color: canvasCssValue('--theme-tertiary-text', '#999999'),
      }}
    >
      <Rect
        borderRadius={CARD_BORDER_RADIUS}
        border={canvasCssValue('--theme-primary-border', '#e1e1e1')}
        fill={{ color: Color(themeBackground).hex(), opacity }}
        width={width}
        height={height}
      />

      <FlexColumn
        width={width}
        padding={CARD_PADDING}
        clip={{
          x: 0,
          y: 0,
          width,
          height,
        }}
        setRenderedDimensions={setRenderedDimensions}
        horizontalAlignment='stretch'
        gap={4}
      >
        {record.type === 'CLIP' && (
          <FlexRow padding={{ bottom: 8 }} verticalAlignment='center'>
            <Image
              src={
                record.thumbnailUrl
                  ? record.thumbnailUrl
                  : svgToURL(InterviewPlaceholderImage)
              }
              width={thumbnailImageWidth}
              height={225}
              objectFitCover
            />
          </FlexRow>
        )}
        <FlexRow gap={2} verticalAlignment='center'>
          <Rect
            width={30}
            height={20}
            borderRadius={CARD_BORDER_RADIUS}
            fill={{ color: iconBackgroundColor(record), opacity }}
          >
            {icon(record)}
          </Rect>

          <Rect flexGrow />

          <UserAvatar
            initials={record.user.initials}
            name={record.user.name}
            size={20}
            src={record.user.avatarUrl}
          />
        </FlexRow>
        <FlexRow maxRows={2} verticalAlignment='center'>
          <Text
            key='name'
            color={canvasCssValue('--theme-primary-text', '#333333')}
            lineHeight={RECORD_LINE_HEIGHT}
            size={HIGHLIGHT_FONT_SIZE}
            maxWidth={width - CARD_PADDING.horizontal * 2 - 30 - 2}
            weight={600}
          >
            {sanitizedText || 'Unknown highlight'}
          </Text>
        </FlexRow>
        <FlexRow
          minHeight={4}
          verticalAlignment='center'
          width={width - CARD_PADDING.horizontal * 2}
          wrap
        >
          <Text lineHeight={RECORD_LINE_HEIGHT} size={HIGHLIGHT_FONT_SIZE}>
            {record.highlightText}
          </Text>
        </FlexRow>
      </FlexColumn>
    </Style>
  );
};

export default HighlightCard;
